import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, MapPin } from "lucide-react";
import { Countdown } from "./Countdown";

const lineVariants = {
  hidden: { y: "110%", opacity: 0 },
  show: (i) => ({
    y: "0%",
    opacity: 1,
    transition: { duration: 1.1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MaskedLine = ({ index, children, className = "" }) => (
  <span className="mask-line">
    <motion.span
      className={`block ${className}`}
      custom={index}
      variants={lineVariants}
      initial="hidden"
      animate="show"
    >
      {children}
    </motion.span>
  </span>
);

const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { delay, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = ({ party }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const cardR = useTransform(scrollYProgress, [0, 1], [2, -3]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <header ref={ref} className="relative min-h-screen flex flex-col overflow-hidden bg-ink" data-testid="hero-section">
      <div className="flex flex-1 flex-col lg:flex-row items-center gap-16 lg:gap-6 px-6 md:px-16 lg:px-24 pt-20 md:pt-24 pb-20">
        <div className="flex-1 w-full">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-accent text-3xl md:text-4xl text-ember mb-4 md:mb-5"
            data-testid="hero-eyebrow"
          >
            you're invited!
          </motion.p>

          <h1 className="font-display leading-[0.9] tracking-tight">
            <MaskedLine index={1} className="text-6xl md:text-7xl lg:text-[8vw] italic font-semibold text-ember pb-2 md:pb-4">
              Soup
            </MaskedLine>
            <MaskedLine index={2} className="text-6xl md:text-7xl lg:text-[8vw] font-black text-forest pb-2 md:pb-4">
              Party<span className="text-harvest">.</span>
            </MaskedLine>
          </h1>

          <motion.p {...fadeUp(0.95)} className="mt-6 max-w-md font-body text-base md:text-lg text-fog leading-relaxed">
            A creamy soup exchange filled with mouthwatering flavors, laughter, and entertainment.
          </motion.p>

          <motion.div {...fadeUp(1.05)} className="mt-7 border-l-2 border-ember pl-5 space-y-1">
            <p className="font-display text-xl md:text-2xl text-cream" data-testid="hero-date">
              {party?.date_display || "Saturday, October 3, 2026"} · {party?.time_display || "5:00 PM"}
            </p>
            <div className="flex items-start gap-2 font-body text-sm text-fog" data-testid="hero-location">
              <MapPin size={14} className="text-ember mt-1 shrink-0" />
              <span>
                {party?.location || "Aymond House"}
                <br />
                {party?.location_note || "18056 Shadow Creek Ave."}
                <br />
                {party?.location_city || "Baton Rouge, LA 70816"}
              </span>
            </div>
          </motion.div>

          <motion.div {...fadeUp(1.15)} className="mt-9">
            <p className="font-body text-[11px] uppercase tracking-[0.3em] text-fog mb-3">Countdown to ladles up</p>
            <Countdown />
          </motion.div>

          <motion.div {...fadeUp(1.25)} className="mt-9 flex flex-wrap gap-4">
            <button data-testid="hero-rsvp-btn" onClick={() => scrollTo("rsvp")} className="btn-ember">
              RSVP Now
            </button>
            <button data-testid="hero-details-btn" onClick={() => scrollTo("details")} className="btn-ghost">
              The Details
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 60, rotate: 7 }}
          animate={{ opacity: 1, y: 0, rotate: 2 }}
          transition={{ delay: 0.5, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          style={{ y: cardY, rotate: cardR }}
          className="relative shrink-0"
        >
          <div className="border-[10px] border-[#FFFDF7] bg-[#FFFDF7] shadow-2xl shadow-ember/25">
            <img
              src="/invitation.png"
              alt="The official Soup Party invitation"
              className="block w-72 md:w-80 lg:w-[380px]"
              data-testid="hero-invitation-image"
            />
          </div>
          <p className="absolute -bottom-7 left-1/2 -translate-x-1/2 -rotate-3 whitespace-nowrap font-accent text-2xl text-harvest">
            save this image & invite a friend!
          </p>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="pb-8 flex justify-center"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ChevronDown className="text-fog" size={22} />
        </motion.div>
      </motion.div>
    </header>
  );
};
